<template>
  <div>
    <h2 class="mb-4">
      <span v-if="isEditMode">"{{ editedEntity.name | translate }}" {{ $t("common.update") }}</span>
      <span v-else>{{ $t("report-template.new") }}</span>
      <a href="/management/report-template" class="float-right"> <i class="fa fa-chevron-left"></i> {{ $t("report-template.library") }}</a>
    </h2>

    <b-row>
      <b-col>
        <b-form-tags v-model="form.products" size="lg" class="mb-2" add-on-change no-outer-focus>
          <template v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }">
            <b-form-select v-bind="inputAttrs" v-on="inputHandlers" :disabled="disabled || products.length === 0" :options="products">
              <template #first>
                <!-- This is required to prevent bugs with Safari -->
                <option disabled value=""> {{ $t("product.text") }}</option>
              </template>
            </b-form-select>

            <ul v-if="tags.length > 0" class="list-inline d-inline-block mt-2">
              <li v-for="tag in tags" :key="tag" class="list-inline-item">
                <b-form-tag @remove="removeTag(tag)" :title="tag" :disabled="disabled" variant="primary">{{ tag }}</b-form-tag>
              </li>
            </ul>
            <b-form-text v-else>
              <span class="text-danger">{{ $t("product.required") }}</span>
            </b-form-text>
          </template>
        </b-form-tags>

        <h6 class="mt-4">{{ $t("report-template.type.text") }}</h6>
        <select v-model="form.type" class="form-control">
          <option value="ACCESS_AND_RESTRICTION">{{ $t("report-template.type.access-and-restrictions") }}</option>
          <option value="DOCUMENT_WITH_TEMPLATE">{{ $t("report-template.type.document-with-template") }}</option>
          <option value="RISK_ANALYSIS">{{ $t("report-template.type.risk-analysis") }}</option>
          <option value="ASSET_GROUP_CRITICALITY">{{ $t("report-template.type.asset-group-criticality") }}</option>
          <option value="GAP_ANALYSIS">{{ $t("report-template.type.gap-analysis") }}</option>
          <option value="NOT_APPLICABLE_POLICIES">{{ $t("report-template.type.not-applicable-policies") }}</option>
          <option value="VERBIS">{{ $t("report-template.type.verbis") }}</option>
        </select>

        <h6 class="mt-2">{{ $t("common.language") }}</h6>
        <multiselect
          v-model="form.language"
          track-by="language"
          :placeholder="$t('common.language-select')"
          :options="languages"
          :searchable="false"
          :allow-empty="false"
          :show-labels="false"
        >
          <template slot="singleLabel" slot-scope="{ option }">
            <img :src="`${option.flag}`" alt="user-image" class="mr-1" height="12" />
            <span class="align-middle">{{ option.title[option.language] }}</span>
          </template>
          <template slot="option" slot-scope="{ option }">
            <img :src="`${option.flag}`" alt="user-image" class="mr-1" height="12" />
            <span class="align-middle">{{ option.title[option.language] }}</span>
          </template>
        </multiselect>

        <sweet-input
          class="mt-3"
          v-model="form.name"
          :isValid="!$v.form.name.$error"
          @input="$v.form.name.$touch()"
          layout="vertical"
          :placeholder="$t('report-template.name')"
          :label="$t('report-template.name')"
        />

        <sweet-textarea
          class="mt-3"
          v-model="form.description"
          :isValid="!$v.form.description.$error"
          @input="$v.form.description.$touch()"
          type="text"
          layout="vertical"
          :placeholder="$t('common.description')"
          :label="$t('common.description')"
          :rows="10"
        />

        <sweet-button @click="save()" type="submit" :loading="isUpdating" :disabled="isUpdating || $v.$invalid" variant="success" icon="mdi mdi-check" buttonClass="mt-2">
          {{ $t("common.save") }}
        </sweet-button>
      </b-col>
      <b-col offset="1"> </b-col>
    </b-row>
    <hr />

    <h3 class="text-center m-5">{{ $t("report-template.text") }}</h3>
    <Editor ref="editor" magicMenuType="report" />
  </div>
</template>

<script>
import Editor from "@c/document-editor/document-editor";
import SweetTextarea from "@c/form/sweet-textarea";
import { maxLength, minLength, required } from "vuelidate/lib/validators";

const state = "managementReportTemplate";

export default {
  components: { Editor, SweetTextarea },
  data() {
    return {
      form: {
        name: undefined,
        products: undefined,
        description: undefined,
        type: "DOCUMENT_WITH_TEMPLATE",
        content: [],
      },
    };
  },
  validations: {
    form: {
      language: {
        required,
      },
      products: {
        validate() {
          return this.form.products && this.form.products.length > 0;
        },
      },
      name: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(350),
      },
      description: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(4000),
      },
    },
  },

  computed: {
    isUpdating() {
      return this.$store.getters[`${state}/isUpdating`];
    },
    editedEntity() {
      return this.$store.getters[`${state}/editedEntity`];
    },
    isEditMode() {
      return this.$store.getters[`${state}/isEditMode`];
    },
    languages() {
      return this.$store.getters["localization/languages"];
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      const id = this.$route.params.id;
      if (id) {
        const { data } = await this.$store.dispatch(`${state}/findById`, id);

        this.form = {
          ...data,
          language: this.languages.find((i) => i.language == data.language),
          name: data.name[this.currentLanguage],
          description: data.description[this.currentLanguage],
        };
        this.$refs.editor.content = data.content;
      }
    },
    async save() {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch(`${state}/save`, { ...this.form, content: this.$refs.editor.getEditorContent(), language: this.form.language.language });
        this.$toast.success(this.$t("common.success"));

        setTimeout(() => {
          this.$router.push(`/management/report-template`);
        }, 1000);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
  },
};
</script>
